import React, { useEffect } from "react";
import useForm, { FormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile } from "../../../actions/userActions";

// Material-UI imports
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  makeStyles,
  ThemeProvider,
  createMuiTheme
} from '@material-ui/core';
import { Save as SaveIcon } from '@material-ui/icons';

// Form components (these will be updated to use Material-UI)
import AreaField from "../../form/AreaField";
import TextField from "../../form/TextField";
import SwitchField from "../../form/SwitchField";

// Create a theme that matches the current styling
const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#3273dc',
      contrastText: '#fff',
    },
    secondary: {
      main: '#f5f5f5',
    },
    text: {
      primary: '#26344a',
      secondary: '#7a7a7a',
    },
    background: {
      default: '#fffbf9',
      paper: '#fff',
    },
  },
  typography: {
    fontFamily: "'Kosugi Maru', 'Noto Sans JP', sans-serif",
    h4: {
      fontSize: '2.4rem',
      fontWeight: 700,
      color: '#3273dc',
      marginBottom: '2rem',
    },
    body1: {
      fontSize: '1.8rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '1.6rem',
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 8,
  },
  overrides: {
    MuiContainer: {
      root: {
        maxWidth: '600px',
        margin: '0 auto',
        padding: '2rem',
      },
    },
    MuiPaper: {
      root: {
        padding: '2rem',
        backgroundColor: '#fff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    },
    MuiButton: {
      root: {
        fontSize: '1.25rem',
        padding: '0.75rem 2rem',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
        },
      },
      containedPrimary: {
        backgroundColor: '#3273dc',
        color: '#fff',
        '&:hover': {
          backgroundColor: '#2759a5',
        },
      },
    },
    MuiFormControl: {
      root: {
        marginBottom: '2rem',
        width: '100%',
      },
    },
    MuiTextField: {
      root: {
        marginBottom: '2rem',
        '& .MuiOutlinedInput-root': {
          fontSize: '1.6rem',
          '& fieldset': {
            borderColor: '#dbdbdb',
          },
          '&:hover fieldset': {
            borderColor: '#3273dc',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#3273dc',
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: '1.8rem',
          fontWeight: 700,
          color: '#26344a',
          '&.Mui-focused': {
            color: '#3273dc',
          },
        },
        '& .MuiInputBase-input': {
          fontSize: '1.6rem',
        },
        '& .MuiInputBase-inputMultiline': {
          fontSize: '1.6rem',
          minHeight: '120px',
        },
      },
    },
    MuiFormControlLabel: {
      root: {
        margin: '0.5rem 0',
        padding: '0.5rem',
        background: '#f5f5f5',
        borderRadius: '4px',
        width: '100%',
        '& .MuiFormControlLabel-label': {
          fontSize: '1.6rem',
          fontWeight: 600,
          color: '#26344a',
        },
      },
    },
    MuiSwitch: {
      root: {
        width: 40,
        height: 24,
        padding: 0,
      },
      switchBase: {
        padding: 4,
        '&$checked': {
          transform: 'translateX(16px)',
          color: '#fff',
          '& + $track': {
            backgroundColor: '#4caf50',
            opacity: 1,
            border: 'none',
          },
        },
      },
      thumb: {
        width: 16,
        height: 16,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      },
      track: {
        borderRadius: 24,
        backgroundColor: '#ccc',
        opacity: 1,
        transition: 'background-color 0.4s',
      },
      checked: {},
    },
    MuiFormHelperText: {
      root: {
        fontSize: '1.4rem',
        marginTop: '0.25rem',
        '&.Mui-error': {
          color: '#ff3860',
        },
      },
    },
  },
});

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '2rem',
    backgroundColor: '#fffbf9',
    minHeight: '100vh',
  },
  paper: {
    padding: '2rem',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderRadius: '8px',
  },
  title: {
    fontSize: '2.4rem',
    fontWeight: 700,
    color: '#3273dc',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  form: {
    '& .MuiFormControl-root': {
      marginBottom: '2rem',
      width: '100%',
    },
  },
  fieldContainer: {
    marginBottom: '2rem',
  },
  smallFieldContainer: {
    margin: '0.5rem 0',
    padding: '0.5rem',
    background: '#f5f5f5',
    borderRadius: '4px',
  },
  editSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '2rem',
    paddingTop: '2rem',
    borderTop: '1px solid #f0f0f0',
  },
  submitButton: {
    fontSize: '1.25rem',
    padding: '0.75rem 2rem',
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  infoText: {
    fontSize: '1.4rem',
    color: '#23d160',
    marginTop: '0.25rem',
    fontStyle: 'italic',
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    background: '#f9f9f9',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
  },
  switchLabel: {
    fontSize: '1.6rem',
    fontWeight: 600,
    color: '#26344a',
    flex: 1,
  },
  switchInfo: {
    fontSize: '1.2rem',
    color: '#7a7a7a',
    marginTop: '0.5rem',
    lineHeight: 1.4,
  },
}));

const PersonalProfileEdit = props => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const methods = useForm();
  const { setValue, register } = methods;
  const currentUser = useSelector(state => state.user.currentUser);

  useEffect(() => {
    if (currentUser) {
      let profile = currentUser.profile;
      setValue("name", currentUser.name);
      setValue("introduction", profile.introduction);
      setValue("area", profile.area);
      setValue("is_public", profile.is_public);
      setValue("allowEmail", profile.allowEmail);
    }
  }, [currentUser, setValue, register]);

  const onSubmit = data => {
    let fd = { ...data };
    fd.user_type = "Personal";
    dispatch(updateProfile(fd));
  };

  return (
    <ThemeProvider theme={theme}>
      <Container className={classes.container}>
        <Paper className={classes.paper} elevation={2}>
          <Typography variant="h4" className={classes.title}>
            個人プロフィール編集
          </Typography>

          <FormContext {...methods}>
            <form className={classes.form} onSubmit={methods.handleSubmit(onSubmit)}>
              <div className={classes.fieldContainer}>
                <TextField
                  label="表示名"
                  type="text"
                  placeholder="表示名を入力してください"
                  name="name"
                  registerOptions={{ required: true, maxLength: 50, minLength: 2 }}
                />
              </div>

              <div className={classes.fieldContainer}>
                <TextField
                  label="自己紹介文(1000文字以内)"
                  type="textarea"
                  name="introduction"
                  rows={15}
                  registerOptions={{ minLength: 6, maxLength: 1000 }}
                />
              </div>

              <div className={classes.fieldContainer}>
                <AreaField
                  label="希望勤務地"
                  name="area"
                />
              </div>

              <div className={classes.smallFieldContainer}>
                <SwitchField
                  label="プロフィールを公開する"
                  name="is_public"
                  info="※ON にすると企業・雇用者アカウントユーザーがプロフィールの参照、及びメール送信ができるようになります (求職アカウントユーザーからは見えません)"
                />
              </div>

              <Box className={classes.editSection}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submitButton}
                  startIcon={<SaveIcon />}
                >
                  変更を保存
                </Button>
              </Box>
            </form>
          </FormContext>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PersonalProfileEdit;
